import React, { useState, useEffect } from 'react';
import MobileNavigation from './MobileNavigation';
import './ResponsiveLayout.css';

interface ResponsiveLayoutProps {
  children: React.ReactNode;
  sidebar?: React.ReactNode;
  header?: React.ReactNode;
  className?: string;
  unreadCount?: number;
}

type ViewType = 'map' | 'conversations' | 'profile';

const ResponsiveLayout: React.FC<ResponsiveLayoutProps> = ({
  children,
  sidebar,
  header,
  className = '',
  unreadCount = 0
}) => {
  const [currentView, setCurrentView] = useState<ViewType>('map');
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767.98);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle swipe gestures for mobile navigation
  useEffect(() => {
    if (!isMobile) return;

    let startX = 0;
    let startY = 0;
    let isScrolling = false;

    const handleTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isScrolling = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!startX || !startY) return;

      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = startX - currentX;
      const diffY = startY - currentY;

      // Determine if user is scrolling vertically
      if (Math.abs(diffY) > Math.abs(diffX)) {
        isScrolling = true;
        return;
      }

      // Prevent default horizontal scroll if we're handling swipe
      if (Math.abs(diffX) > 10 && !isScrolling) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!startX || !startY || isScrolling) {
        startX = 0;
        startY = 0;
        return;
      }

      const endX = e.changedTouches[0].clientX;
      const diffX = startX - endX;
      const threshold = 50;

      if (Math.abs(diffX) > threshold) {
        if (diffX > 0) {
          // Swipe left - next view
          handleSwipeLeft();
        } else {
          // Swipe right - previous view
          handleSwipeRight();
        }
      }

      startX = 0;
      startY = 0;
    };

    const handleSwipeLeft = () => {
      const views: ViewType[] = ['map', 'conversations', 'profile'];
      const currentIndex = views.indexOf(currentView);
      const nextIndex = (currentIndex + 1) % views.length;
      setCurrentView(views[nextIndex]);
    };

    const handleSwipeRight = () => {
      const views: ViewType[] = ['map', 'conversations', 'profile'];
      const currentIndex = views.indexOf(currentView);
      const prevIndex = currentIndex === 0 ? views.length - 1 : currentIndex - 1;
      setCurrentView(views[prevIndex]);
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [currentView, isMobile]);

  return (
    <div className={`responsive-layout ${isMobile ? 'mobile' : 'desktop'} ${className}`}>
      {header && (
        <header className="responsive-header">
          {header}
        </header>
      )}
      
      <div className="responsive-content">
        {!isMobile && sidebar && (
          <aside className="responsive-sidebar">
            {sidebar}
          </aside>
        )}
        
        <main className="responsive-main">
          {children}
        </main>
      </div>

      {isMobile && (
        <MobileNavigation
          currentView={currentView}
          onViewChange={setCurrentView}
          unreadCount={unreadCount}
        />
      )}
    </div>
  );
};

export default ResponsiveLayout;